import api from "../api";
import {
  GET_ADS,
  ADD_PRODUCT,
  GET_PRODUCTS,
  DELETE_PRODUCT,
  GET_PRODUCTS_BY_COUNTRYCODE,
  GET_PRODUCTS_BY_ID,
  UPDATE_PRODUCT,
  CLEAR_MESSAGE,
  ADD_PROPERTY,
  UPDATE_PROPERTY,
  GET_ALL_PROPERTIES,
  DELETE_PROPERTY,
  CLEAR_PRODUCTS_BY_ID,
  LINK_PRODUCT,
  PATCH_PROPERTY,
  CLEAR_PRODUCTS
} from "./productTypes";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { setAppMessage } from "../appMessageServices/appMessageActions";
import { apiWriteThunk } from "../apiWriteThunk";
import createAppAsyncThunk from "../createAppAsyncThunk";
import { hasDisplayableError } from "../../utils/getErrorMessage";
import { asCaught, errorPayload } from "../caughtError";
// Response shapes come from the shared server/client contracts (SCRUM-983).
import type { AxiosResponse } from "axios";
import type {
  ProductsResponse,
  ProductByIdResponse,
  ProductCreateResponse,
  ProductUpdateResponse,
  ProductAdsResponse,
  ProductListingPagesResponse,
  PropertiesResponse,
  PropertyCreateResponse,
  PropertyRecoverResponse,
  MessageResponse,
} from "../../../../shared/apiTypes";

/**
 * The GET_ERRORS payload for a failed read: the server's body when the hub would
 * actually SHOW it, else a `{ message }` envelope carrying the transport reason.
 *
 * SCRUM-1277 shipped this as `err?.response?.data ?? { message: err?.message }`,
 * and `??` is the bug: it only falls back on null/undefined. This endpoint answers
 * `res.status(400).json(e)` on a bare Error, which serialises to `{}` — not
 * null, so `??` kept it, and `hasDisplayableError({})` is false because `{}` is
 * what a CLEARED errors slice looks like. On the one path that matters, a real
 * server error, the user was shown nothing at all. Not a regression (nothing was
 * shown before either), but the ticket promised the server's message and on that
 * body it did not arrive.
 *
 * Same guard as `createAppAsyncThunk` (see its comment at :50-54), which learned
 * this in SCRUM-1039 for exactly the same reason.
 */
const displayableErrorPayload = (err: unknown): unknown => {
  const caught = asCaught(err);
  const body = caught?.response?.data;
  return hasDisplayableError(body) ? body : { message: caught?.message };
};

export const clearMessage = () => (dispatch) => {
  dispatch({
    type: CLEAR_MESSAGE,
  });
};
export const clearProductById = () => (dispatch) => {
  dispatch({
    type: CLEAR_PRODUCTS_BY_ID,
  });
};

/****************************** MANAGE GLOBAL PRODUCT *******************************/
// SCRUM-2119. SCRUM-1315's unguarded `err.response.data` is gone with the
// hand-written catch: a transport failure (no response) reaches the hub as
// `{ message }` instead of throwing inside the handler.
export const addProduct = apiWriteThunk(
  "products/add",
  (data) => api.post<ProductCreateResponse>("/api/product/add", data),
  (dispatch, body) => {
    dispatch({ type: ADD_PRODUCT, payload: body });
    dispatch(getAllProducts());
  }
);

//update product
export const updateProduct = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<ProductUpdateResponse> = await api.put(
      "/api/product/update",
      data
    );
    dispatch({
      type: UPDATE_PRODUCT,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};

//patch product => link it to the current website
export const patchProduct = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api.patch(
      "/api/product/patch",
      data
    );
    dispatch({
      type: LINK_PRODUCT,
      payload: res.data,
    });
    dispatch(getProductByIdString(data._id));
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};

//get all products
export const getAllProducts = () => async (dispatch) => {
  try {
    const data: AxiosResponse<ProductsResponse> = await api.get("/api/product");
    dispatch({
      type: GET_PRODUCTS,
      payload: data.data.reverse(),
    });
  } catch (err) {
    // SCRUM-856: previously an unhandled rejection on failure (no catch; the else
    // branch only fired for a falsy resolve) — surface the real error via the hub.
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
  }
};

//clear all products
export const clearAllProducts = () => async (dispatch) => {

  dispatch({
    type: CLEAR_PRODUCTS,
    payload: [],
  });  
};

//get product by id 'string'
const getProductByIdString = (id) => (dispatch) => {
  api
    .get(`/api/product/${id}`)
    .then((res: AxiosResponse<ProductByIdResponse>) => {
      dispatch({
        type: GET_PRODUCTS_BY_ID,
        payload: res.data,
      });
    })

    .catch((err) =>
      // SCRUM-856: carry the real error to the hub instead of a null payload it ignores.
      dispatch({
        type: GET_ERRORS,
        payload: err?.response?.data ?? { message: err?.message },
      })
    );
};


//get product by id
export const getProductById = (id) => async (dispatch) => {
  try {
    const res: AxiosResponse<ProductByIdResponse> = await api.get(`/api/product/${id.id}`);
    
    dispatch({
      type: GET_PRODUCTS_BY_ID,
      payload: res.data,
    });
  } catch (err) {
    // SCRUM-856: carry the real error to the hub instead of a null payload it ignores.
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
  }
};

// Delete product — createAppAsyncThunk (SCRUM-1021 follow-up). A failed DELETE
// now rejects and is surfaced once centrally by appErrorMiddleware (replacing the
// hand-rolled GET_ERRORS catch), and `.unwrap()` lets the caller gate its success
// toast: the legacy thunk swallowed failures, so the products screen reported a
// false success on a failed delete. The success dispatches (optimistic
// DELETE_PRODUCT + refetch) are unchanged.
export const deleteProduct = createAppAsyncThunk(
  "products/deleteProduct",
  async (id: string, { dispatch }) => {
    await api.delete<MessageResponse>(`/api/product/delete/${id}`);
    dispatch({ type: DELETE_PRODUCT, payload: id });
    dispatch(getAllProducts());
    dispatch(getProductsByCountryCode());
    return id;
  }
);

// SCRUM-1303: the listing pages an unlink (siteIds = the site being unlinked) or
// a delete (siteIds empty = every site) would change — what the confirmation
// dialog names before the action runs. A plain read, not a thunk: nothing in the
// store wants it, and the two dialogs consume it locally.
export const fetchProductListingPages = async (
  id: string,
  siteIds: string[] = []
): Promise<ProductListingPagesResponse> => {
  const query = siteIds.length ? `?sites=${siteIds.map(encodeURIComponent).join(",")}` : "";
  const res: AxiosResponse<ProductListingPagesResponse> = await api.get(
    `/api/product/${id}/listing-pages${query}`
  );
  return Array.isArray(res.data) ? res.data : [];
};

//get product Ads
export const getProductAds = (id) => async (dispatch) => {
  let res: AxiosResponse<ProductAdsResponse>;
  // Keep the GET_ADS dispatch out of the try: reducers run synchronously inside
  // dispatch(), so a reducer/render error would land in the catch below and be
  // mislabelled as a network failure (SCRUM-948 / SCRUM-1200).
  try {
    res = await api.get(`/api/product/product/ads/${id}`);
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: displayableErrorPayload(err),
    });
    return;
  }
  return dispatch({
    type: GET_ADS,
    payload: Array.isArray(res.data) ? res.data : [],
  });
};
//get products by country code
export const getProductsByCountryCode = () => async (dispatch) => {
  try {
    const res: AxiosResponse<ProductsResponse> = await api.get("/api/product/products/countrycode");
    dispatch({
      type: GET_PRODUCTS_BY_COUNTRYCODE,
      payload: res.data,
    });
  } catch (err) {
    // SCRUM-856: surface the real error via the hub instead of nulling the
    // country-code product data on a fetch failure.
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
  }
};

/****************************** MANAGE PROPERTIES *******************************/
//add property
export const addProperty = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<PropertyCreateResponse> = await api
      .post("/api/property/add", data);
    dispatch({
      type: ADD_PROPERTY,
      payload: res.data,
    });
    dispatch(getAllProperties());
    // Final, ready-to-show success message on the appMessage channel; the global
    // notification hub renders it. Returning res.data gives the container an own
    // signal to reset its form on, decoupled from the shared slice (SCRUM-883).
    dispatch(setAppMessage("success", "Property Added Successfully"));
    return res.data;
  } catch (err) {
    // SCRUM-1819: a 409 naming `deletedId` means only a soft-deleted property
    // holds this name. Hand that record to the caller, which offers Restore /
    // Start fresh in the form; the generic "no duplicate name" toast would
    // contradict the offer, so it is not raised on this path.
    const holder = asCaught(err)?.response?.data?.errors?.[0];
    if (asCaught(err)?.response?.status === 409 && holder?.deletedId) {
      return { deletedHolder: holder };
    }
    // Final message on the errors slice (no sentinel round-trip); the hub renders
    // it. Return null so the caller knows the add failed and keeps the form.
    dispatch({
      type: GET_ERRORS,
      payload: "Enter a valid Property with no duplicate name",
    });
    return null;
  }
};

// SCRUM-1819: the two answers to the 409 addProperty gets when only a deleted
// property holds the name. Both reject to the central hub on failure, so the
// form keeps its offer on screen; callers `.unwrap()` them.

// Restore: bring the deleted property back under its reserved name.
export const restoreProperty = createAppAsyncThunk(
  "products/restoreProperty",
  async (id: string, { dispatch }) => {
    const res: AxiosResponse<PropertyRecoverResponse> = await api.post(`/api/property/${id}/restore`);
    dispatch(getAllProperties());
    return res.data;
  }
);

// Start fresh: release the deleted property's name for a new property.
export const releaseProperty = createAppAsyncThunk(
  "products/releaseProperty",
  async (id: string) => {
    const res: AxiosResponse<PropertyRecoverResponse> = await api.post(`/api/property/${id}/release`);
    return res.data;
  }
);

//update property
export const updateProperty = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api
      .put("/api/property/update", data);
    dispatch({
      type: UPDATE_PROPERTY,
      payload: res.data,
    }); dispatch(getAllProperties());
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: displayableErrorPayload(err),
    });
  }
};

//get all properties
export const getAllProperties = () => async (dispatch) => {
  let res: AxiosResponse<PropertiesResponse>;
  try {
    res = await api.get("/api/property");
  } catch (err) {
    // `payload: null` reads as CLEARED to the errors slice (hasDisplayableError),
    // so the hub rendered nothing at all and the server's reason was discarded —
    // the same defect the deleteProperty note below records for its sibling.
    dispatch({
      type: GET_ERRORS,
      payload: displayableErrorPayload(err),
    });
    return;
  }
  return dispatch({
    type: GET_ALL_PROPERTIES,
    payload: Array.isArray(res.data) ? res.data.reverse() : [],
  });
};

// Delete property — createAppAsyncThunk (SCRUM-1021 follow-up). A failed DELETE
// now rejects and is surfaced once centrally by appErrorMiddleware (the legacy
// catch discarded the real error with `payload: null`), and `.unwrap()` lets the
// caller gate its success toast instead of firing it unconditionally.
export const deleteProperty = createAppAsyncThunk(
  "products/deleteProperty",
  async (id: string, { dispatch }) => {
    await api.delete<unknown>(`/api/property/delete/${id}`);
    dispatch({ type: DELETE_PROPERTY, payload: id });
    dispatch(getAllProperties());
    return id;
  }
);

//patch property => Link it to the current website
// Resolves true when the PATCH was stored and false when it was refused, so a
// caller can confirm success only on success (SCRUM-1803). The refusal itself
// still reaches the editor through GET_ERRORS and the global notification hub.
export const patchProperty = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api
      .patch("/api/property/patch", data);
    dispatch({
      type: PATCH_PROPERTY,
      payload: res.data,
    }); dispatch(getAllProperties());
    return true;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: displayableErrorPayload(err),
    });
    return false;
  }
};
